import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Car, Building, Moon, Camera } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import ImageWithLoader from './ImageWithLoader';

// Content for each category
const categoryContent = {
  safaris: {
    image: "https://imgur.com/3zSo08L.jpg",
  },
  cityTours: {
    image: "https://imgur.com/OJoUqFM.jpg",
  },
  selfDrive: {
    image: "https://imgur.com/4IviNqy.jpg",
  },
  nightCrawlers: {
    image: "https://imgur.com/vfIz6yT.jpg",
  }
};

interface HeroProps {
  onCategoryChange?: (category: 'safaris' | 'cityTours' | 'selfDrive' | 'nightCrawlers') => void;
}

const Hero: React.FC<HeroProps> = ({ onCategoryChange }) => {
  const [activeCategory, setActiveCategory] = useState<'safaris' | 'cityTours' | 'selfDrive' | 'nightCrawlers'>('safaris');
  const { t } = useTranslation();

  // Handle category change and notify parent component
  const handleCategoryChange = (category: 'safaris' | 'cityTours' | 'selfDrive' | 'nightCrawlers') => {
    setActiveCategory(category);
    if (onCategoryChange) {
      onCategoryChange(category);
    }
  };

  return (
    <div className="relative h-[80vh] w-full bg-black overflow-hidden flex flex-col">
      {/* Dynamic Background */}
      <AnimatePresence mode="wait">
        <motion.div 
          key={activeCategory}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.8 }}
          className="absolute inset-0" 
          style={{ zIndex: 1 }}
        >
          <img
            src="https://imgur.com/gbRlk9n.jpg"
            alt="Uganda landscape"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/50 to-black/80" />
        </motion.div>
      </AnimatePresence>

      {/* Content */}
      <div className="relative z-10 flex-grow flex flex-col">
        <div className="max-w-7xl mx-auto px-4 pt-20 pb-12 sm:px-6 lg:px-8 flex-grow flex flex-col">
          {/* Main Title with Animation - Positioned on the left */}
          <div className="absolute left-4 sm:left-6 lg:left-8 top-20 max-w-md">
            <motion.h1 
              className="text-2xl sm:text-3xl md:text-4xl font-bold text-white leading-tight"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <span className="inline-block">
                <span className="text-ta-green">Explore</span>{' '}
                <span className="relative">
                  Uganda
                  <motion.span 
                    className="absolute -bottom-1 left-0 w-full h-0.5 bg-ta-green rounded-full"
                    initial={{ scaleX: 0 }}
                    animate={{ scaleX: 1 }}
                    transition={{ duration: 1.2, delay: 0.5 }}
                  ></motion.span>
                </span>
              </span>
              <span className="block mt-1 text-sm sm:text-base md:text-lg">with Local Guides</span>
            </motion.h1>
          </div>
        </div>

        {/* Category Buttons - Fixed at bottom */}
        <motion.div 
          className="w-full bg-black/30 backdrop-blur-sm py-4 border-t border-b border-white/10 mt-auto"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.6 }}
        >
          <div className="flex justify-center space-x-0 max-w-7xl mx-auto">
            <button
              onClick={() => handleCategoryChange('safaris')}
              className={`flex-1 px-2 sm:px-6 py-4 text-center border-b-2 transition-all duration-300 ${
                activeCategory === 'safaris' 
                  ? 'border-ta-green text-ta-green' 
                  : 'border-transparent text-white/70 hover:text-white hover:border-white/50'
              }`}
            >
              <div className="flex flex-col items-center justify-center">
                <Camera className={`h-5 w-5 sm:h-6 sm:w-6 mb-1 ${activeCategory === 'safaris' ? 'text-ta-green' : ''}`} />
                <span className="font-medium text-xs sm:text-sm">Safaris</span>
              </div>
            </button>
            
            <button
              onClick={() => handleCategoryChange('cityTours')}
              className={`flex-1 px-2 sm:px-6 py-4 text-center border-b-2 transition-all duration-300 ${
                activeCategory === 'cityTours' 
                  ? 'border-ta-green text-ta-green' 
                  : 'border-transparent text-white/70 hover:text-white hover:border-white/50'
              }`}
            >
              <div className="flex flex-col items-center justify-center">
                <Building className={`h-5 w-5 sm:h-6 sm:w-6 mb-1 ${activeCategory === 'cityTours' ? 'text-ta-green' : ''}`} />
                <span className="font-medium text-xs sm:text-sm">City Tours</span>
              </div>
            </button>
            
            <button
              onClick={() => handleCategoryChange('selfDrive')}
              className={`flex-1 px-2 sm:px-6 py-4 text-center border-b-2 transition-all duration-300 ${
                activeCategory === 'selfDrive' 
                  ? 'border-ta-green text-ta-green' 
                  : 'border-transparent text-white/70 hover:text-white hover:border-white/50'
              }`}
            >
              <div className="flex flex-col items-center justify-center">
                <Car className={`h-5 w-5 sm:h-6 sm:w-6 mb-1 ${activeCategory === 'selfDrive' ? 'text-ta-green' : ''}`} />
                <span className="font-medium text-xs sm:text-sm">Self-drive</span>
              </div>
            </button>
            
            <button
              onClick={() => handleCategoryChange('nightCrawlers')}
              className={`flex-1 px-2 sm:px-6 py-4 text-center border-b-2 transition-all duration-300 ${
                activeCategory === 'nightCrawlers' 
                  ? 'border-ta-green text-ta-green' 
                  : 'border-transparent text-white/70 hover:text-white hover:border-white/50'
              }`}
            >
              <div className="flex flex-col items-center justify-center">
                <Moon className={`h-5 w-5 sm:h-6 sm:w-6 mb-1 ${activeCategory === 'nightCrawlers' ? 'text-ta-green' : ''}`} />
                <span className="font-medium text-xs sm:text-sm">Night Crawl</span>
              </div>
            </button>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Hero;